export const VIEWER_PREFERENCES_KEY = "neoma.signalViewer.preferences.v1";

export interface ViewerPreferences {
  separateMultichannelSignals: boolean;
  showSelectedTotalTime: boolean;
  alignTimelineToSignalViewport: boolean;
  highlightActiveSleepStage: boolean;
  cursorFollowsMouse: boolean;
  link3dViews: boolean;
}

export const DEFAULT_VIEWER_PREFERENCES: Readonly<ViewerPreferences> = Object.freeze({
  separateMultichannelSignals: true,
  showSelectedTotalTime: true,
  alignTimelineToSignalViewport: false,
  highlightActiveSleepStage: false,
  cursorFollowsMouse: false,
  link3dViews: true,
});

type StoredViewerPreferences = ViewerPreferences & { version: 1 };

const preferenceNames = Object.keys(DEFAULT_VIEWER_PREFERENCES) as Array<keyof ViewerPreferences>;

export function parseViewerPreferences(raw: string | null): ViewerPreferences {
  if (raw == null) return { ...DEFAULT_VIEWER_PREFERENCES };
  try {
    const value = JSON.parse(raw) as Partial<StoredViewerPreferences> | null;
    if (!value || typeof value !== "object" || value.version !== 1) return { ...DEFAULT_VIEWER_PREFERENCES };
    if (preferenceNames.some((name) => typeof value[name] !== "boolean")) return { ...DEFAULT_VIEWER_PREFERENCES };
    return Object.fromEntries(preferenceNames.map((name) => [name, value[name]])) as unknown as ViewerPreferences;
  } catch {
    return { ...DEFAULT_VIEWER_PREFERENCES };
  }
}

export function serializeViewerPreferences(preferences: ViewerPreferences): string {
  const stored: StoredViewerPreferences = { version: 1, ...preferences };
  return JSON.stringify(stored);
}

function availableStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function loadViewerPreferences(storage?: Pick<Storage, "getItem"> | null): ViewerPreferences {
  const source = storage === undefined ? availableStorage() : storage;
  if (!source) return { ...DEFAULT_VIEWER_PREFERENCES };
  try {
    return parseViewerPreferences(source.getItem(VIEWER_PREFERENCES_KEY));
  } catch {
    return { ...DEFAULT_VIEWER_PREFERENCES };
  }
}

export function saveViewerPreferences(
  preferences: ViewerPreferences,
  storage?: Pick<Storage, "setItem"> | null,
): void {
  const destination = storage === undefined ? availableStorage() : storage;
  if (!destination) return;
  try {
    destination.setItem(VIEWER_PREFERENCES_KEY, serializeViewerPreferences(preferences));
  } catch {
    // Viewing remains usable when storage is unavailable or full.
  }
}
